import { useRef, useState } from "react";

import { Row, useAnalytics, useAnalyticsAttributes } from "../../components";
import { UiTabPanelModel } from "../../models";
import { renderViewModels } from "../../render-view-model";

import { Tab, TabList, TabPanel, Tabs, Title } from "./UiTabPanel.styles";

export type UiTabPanelProps = {
    readonly model: UiTabPanelModel;
};

const UiTabPanel = ({ model }: UiTabPanelProps) => {
    const tabs = model.content.tabs.filter(({ type }) => type === "uiTab");

    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    const tabRefs = useRef<HTMLButtonElement[]>([]);

    const [index, setIndex] = useState(0);
    const [focusIndex, setFocusIndex] = useState(0);
    const firstTab = 0;
    const lastTab = tabs.length - 1;

    const moveFocusToTab = (currentTab: number) => {
        const tabToFocus = tabRefs.current[currentTab];
        tabToFocus && tabToFocus.focus();
        setFocusIndex(currentTab);
    };

    const moveToIndex = (newIndex: number) => {
        if (newIndex < 0) {
            moveFocusToTab(lastTab);
        } else if (newIndex > lastTab) {
            moveFocusToTab(firstTab);
        } else {
            moveFocusToTab(newIndex);
        }
    };

    const onKeydown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        let stopEventBubbling = true;

        switch (event.key) {
            case "ArrowLeft":
                moveToIndex(focusIndex - 1);
                break;
            case "ArrowRight":
                moveToIndex(focusIndex + 1);
                break;
            case "Home":
                moveToIndex(firstTab);
                break;
            case "End":
                moveToIndex(lastTab);
                break;

            default:
                stopEventBubbling = false;
                break;
        }

        if (stopEventBubbling) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    return (
        <>
            <Tabs>
                <TabList role="tablist" aria-labelledby={`tablist-${model.id}`}>
                    {tabs.map((tab, i) => (
                        <Tab
                            id={`tab-${i}-${model.id}`}
                            key={i}
                            tabIndex={i !== index ? -1 : 0}
                            ref={(element) => {
                                if (element) {
                                    tabRefs.current[i] = element;
                                }
                            }}
                            aria-selected={i === index}
                            type="button"
                            role="tab"
                            aria-controls={`tabpanel-${i}}-${model.id}`}
                            onKeyDown={(event) => onKeydown(event)}
                            onClick={() => {
                                setIndex(i);
                                sendTrackEvent("tabpanelTabClicked", {
                                    ...analyticsAttributes,
                                    eventContainer: model.type,
                                    event: "clicked",
                                    eventComponent: "tab",
                                    label: tab.content.buttonText,
                                    labelEnglish: tab.content.entryTitle,
                                    uuid: tab.id
                                });
                            }}
                        >
                            {tab.content.buttonText}
                        </Tab>
                    ))}
                </TabList>
            </Tabs>

            {tabs.map((tab, i) => (
                <TabPanel
                    className={i !== index ? "is-hidden" : ""}
                    id={`tabpanel-${i}-${model.id}`}
                    key={i}
                    role="tabpanel"
                    aria-labelledby={`tab-${i}-${model.id}`}
                >
                    {model.content.tabs[i]?.content.title && (
                        <Title>{model.content.tabs[i]?.content.title}</Title>
                    )}
                    <Row>{renderViewModels(tab.content.columns)}</Row>
                </TabPanel>
            ))}
        </>
    );
};

export default UiTabPanel;
